import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search, RefreshCw, Eye, Plus, Mail, Phone } from "lucide-react";
import AddContactDialog from "@/components/AddContactDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactsPage = () => {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchContacts();
  }, [search]);

  const fetchContacts = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      const response = await axios.get(`${API}/contacts?${params.toString()}`);
      setContacts(response.data);
      setLoading(false);
    } catch (error) {
      toast.error("Failed to fetch contacts");
      setLoading(false);
    }
  };

  if (loading) return <div className="text-center py-12">Loading...</div>;

  return (
    <div className="space-y-6 fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{fontFamily: 'Space Grotesk, sans-serif'}}>Contacts</h2>
          <p className="text-sm sm:text-base text-slate-600 mt-1">Manage your contact database</p>
        </div>
        <div className="flex space-x-2">
          <AddContactDialog onContactAdded={fetchContacts} />
          <Button variant="outline" onClick={fetchContacts} className="px-3">
            <RefreshCw className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <Input
          type="text"
          placeholder="Search contacts by name, email, or title..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-12 h-12"
        />
      </div>

      <div className="glass-card rounded-xl p-5 text-center">
        <p className="text-2xl font-bold text-slate-900">{contacts.length}</p>
        <p className="text-sm text-slate-600">Total Contacts</p>
      </div>

      {contacts.length === 0 ? (
        <div className="glass-card rounded-xl p-12 text-center">
          <h3 className="text-xl font-semibold mb-2">No contacts found</h3>
          <AddContactDialog onContactAdded={fetchContacts} />
        </div>
      ) : (
        <div className="glass-card rounded-xl overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Company</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {contacts.map((contact) => (
                <TableRow key={contact.id}>
                  <TableCell className="font-medium">{contact.name}</TableCell>
                  <TableCell>{contact.title || 'N/A'}</TableCell>
                  <TableCell>{contact.company_name || 'N/A'}</TableCell>
                  <TableCell>
                    <div className="flex items-center space-x-2">
                      {contact.email && <Mail className="w-3 h-3 text-slate-400" />}
                      <span className="text-sm">{contact.email || 'N/A'}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center space-x-2">
                      {contact.phone && <Phone className="w-3 h-3 text-slate-400" />}
                      <span className="text-sm">{contact.phone || 'N/A'}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="outline" size="sm" onClick={() => navigate(`/contacts/${contact.id}`)}>
                      <Eye className="w-4 h-4 mr-1" />View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default ContactsPage;
